import { Tab, Tabs, Typography } from "@mui/material";

import { Iconify } from "@onair/minimal-ui/iconify";
import { UseFormReturn, useWatch } from "react-hook-form";
import { WizardSchemaType } from "./index";
import { useMemo, useState } from "react";
import { EventTypeV2 } from "@onair/repositories";
import { StepInfoSchema } from "./step-info";
import { StepResourceSchema } from "./step-resource";
import { StepTimelineSchema } from "./step-timeline";
import { StepPricingSchema } from "./step-pricing";
import { useDebounce } from "react-use";
import { StepTimelineAndPricingSchema } from "./validations/step-timeline-and-pricing-schema.ts";

export interface StepperItem {
	label: string;
	defaultIcon: string | React.ReactNode;
	activeIcon: string | React.ReactNode;
	errorIcon: string | React.ReactNode;
	optional?: boolean;
	hide?: boolean;
}

interface StepperProps {
	onStepClick: (step: number) => void;
	steps: StepperItem[];
	activeStep: number;
	methods: UseFormReturn<WizardSchemaType>;
	isMobile?: boolean;
}

enum TabStatus {
	VALID = "valid",
	ERROR = "error",
	DEFAULT = "default",
}

export const Stepper = ({
	steps,
	methods,
	activeStep,
	onStepClick,
	isMobile = false,
}: StepperProps) => {
	const {
		control,
		getFieldState,
		formState: { errors },
	} = methods;

	const [tabsStatus, setTabsStatus] = useState<
		Partial<Record<keyof WizardSchemaType, TabStatus>>
	>({
		stepInfo: TabStatus.DEFAULT,
		stepTimeline: TabStatus.DEFAULT,
		stepPricing: TabStatus.DEFAULT,
		stepResource: TabStatus.DEFAULT,
		stepTimelineAndPricing: TabStatus.DEFAULT,
	});

	const stepType = useWatch({
		name: "type",
		control,
	});

	const stepInfo = useWatch({
		name: "stepInfo",
		control,
	});

	const stepTimeline = useWatch({
		name: "stepTimeline",
		control,
	});

	const stepPricing = useWatch({
		name: "stepPricing",
		control,
	});

	const stepTimelineAndPricing = useWatch({
		name: "stepTimelineAndPricing",
		control,
	});

	const stepResource = useWatch({
		name: "stepResource",
		control,
	});

	const stepIndexToStepKey: Record<number, keyof WizardSchemaType | undefined> =
		useMemo(() => {
			if (stepType === EventTypeV2.SINGLE) {
				return {
					0: "stepInfo",
					1: "stepTimelineAndPricing",
					2: "stepResource",
				};
			}
			return {
				0: "stepInfo",
				1: "stepTimeline",
				2: "stepPricing",
				3: "stepResource",
			};
		}, [stepType]);

	// VALIDATE INFO
	useDebounce(
		() => {
			const { invalid } = getFieldState("stepInfo");
			const canMarkError = !StepInfoSchema.safeParse(stepInfo).success;

			let tabStatus: TabStatus = TabStatus.DEFAULT;

			if (invalid && canMarkError) tabStatus = TabStatus.ERROR;
			if (!invalid && !canMarkError) tabStatus = TabStatus.VALID;

			setTabsStatus((prev) => ({
				...prev,
				stepInfo: tabStatus,
			}));
		},
		10,
		[stepInfo, errors?.["stepInfo"]],
	);

	// VALIDATE TIMELINE AND PRICING
	useDebounce(
		() => {
			const { invalid } = getFieldState("stepTimelineAndPricing");
			const canMarkError = !StepTimelineAndPricingSchema.safeParse(
				stepTimelineAndPricing,
			).success;

			let tabStatus: TabStatus = TabStatus.DEFAULT;

			if (invalid && canMarkError) tabStatus = TabStatus.ERROR;
			if (!invalid && !canMarkError) tabStatus = TabStatus.VALID;

			setTabsStatus((prev) => ({
				...prev,
				stepTimelineAndPricing: tabStatus,
			}));
		},
		10,
		[stepTimelineAndPricing, errors?.["stepTimelineAndPricing"]],
	);

	// VALIDATE TIMELINE
	useDebounce(
		() => {
			const { invalid } = getFieldState("stepTimeline");
			const canMarkError = !StepTimelineSchema.safeParse(stepTimeline).success;

			let tabStatus: TabStatus = TabStatus.DEFAULT;

			if (invalid && canMarkError) tabStatus = TabStatus.ERROR;
			if (!invalid && !canMarkError) tabStatus = TabStatus.VALID;

			setTabsStatus((prev) => ({
				...prev,
				stepTimeline: tabStatus,
			}));
		},
		10,
		[stepTimeline, errors?.["stepTimeline"]],
	);

	// VALIDATE PRICING
	useDebounce(
		() => {
			const { invalid } = getFieldState("stepPricing");
			const canMarkError = !StepPricingSchema.safeParse(stepPricing).success;

			let tabStatus: TabStatus = TabStatus.DEFAULT;

			if (invalid && canMarkError) tabStatus = TabStatus.ERROR;
			if (!invalid && !canMarkError) tabStatus = TabStatus.VALID;

			setTabsStatus((prev) => ({
				...prev,
				stepPricing: tabStatus,
			}));
		},
		10,
		[stepPricing, errors?.["stepPricing"]],
	);

	// VALIDATE RESOURCES
	useDebounce(
		() => {
			const { invalid, isDirty } = getFieldState("stepResource");

			const canMarkError = !StepResourceSchema.safeParse(stepResource).success;

			let tabStatus: TabStatus = TabStatus.DEFAULT;

			if (invalid && canMarkError) tabStatus = TabStatus.ERROR;
			if (!invalid && !canMarkError && isDirty) tabStatus = TabStatus.VALID;

			setTabsStatus((prev) => ({
				...prev,
				stepResource: tabStatus,
			}));
		},
		10,
		[stepResource, errors?.["stepResource"]],
	);

	const standAtErrorTab =
		tabsStatus[stepIndexToStepKey[activeStep] as keyof WizardSchemaType] ===
		TabStatus.ERROR;

	return (
		<Tabs
			value={activeStep}
			variant={isMobile ? "fullWidth" : "standard"}
			textColor="primary"
			indicatorColor="primary"
			sx={{
				color: standAtErrorTab ? "error.main" : "primary.main",
			}}
			onChange={(_, step) => {
				onStepClick(step);
			}}
		>
			{steps.map(
				(
					{ label, activeIcon, defaultIcon, errorIcon, optional = false },
					index,
				) => {
					const isSelecting = activeStep === index;
					const isValidTab =
						tabsStatus[stepIndexToStepKey[index] as keyof WizardSchemaType] ===
						TabStatus.VALID;
					const isErrorTab =
						tabsStatus[stepIndexToStepKey[index] as keyof WizardSchemaType] ===
						TabStatus.ERROR;
					return (
						<Tab
							sx={{
								color:
									activeStep === index
										? isErrorTab
											? "rgba(240, 68, 56, 1) !important"
											: "#0050FF !important"
										: isErrorTab
											? "rgba(240, 68, 56, 1) !important"
											: "text.secondary",
								fontWeight: 600,
								flexDirection: {
									md: "row",
									xs: "column",
								},
							}}
							key={label}
							label={
								<>
									{isValidTab ? (
										<Iconify
											icon="lets-icons:check-fill"
											color="primary.main"
											width={24}
										/>
									) : (
										<>
											{isErrorTab ? (
												<>{errorIcon}</>
											) : (
												<>{isSelecting ? defaultIcon : activeIcon}</>
											)}
										</>
									)}
									<Typography
										variant="subtitle2"
										fontWeight={600}
										sx={{
											ml: 1,
										}}
									>
										{label}{" "}
										{optional && (
											<Typography
												variant="subtitle2"
												fontWeight={400}
												sx={{
													display: {
														md: "inline",
														xs: "block",
													},
												}}
												color={
													isErrorTab
														? "error.main"
														: isSelecting
															? "primary.main"
															: "text.disabled"
												}
											>
												(không bắt buộc)
											</Typography>
										)}
									</Typography>
								</>
							}
							value={index}
						/>
					);
				},
			)}
		</Tabs>
	);
};
